import * as SentrySDK from '@sentry/react'

// PII fields that must never reach Sentry — sanitized in beforeSend and
// when building captureException context objects.
const PII_KEYS = new Set([
  'client_name',
  'client_email',
  'client_phone',
  'clientName',
  'clientEmail',
  'clientPhone',
  'name',
  'email',
  'phone',
])

function redactValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(redactValue)
  }

  if (value !== null && typeof value === 'object') {
    return redactObject(value as Record<string, unknown>)
  }

  return value
}

function redactObject(obj: Record<string, unknown>): Record<string, unknown> {
  const redacted: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (PII_KEYS.has(key)) {
      redacted[key] = '[Redacted]'
    } else {
      redacted[key] = redactValue(value)
    }
  }

  return redacted
}

/**
 * Initialize Sentry. Reads DSN/environment/release from Vite env vars.
 * No-ops when VITE_SENTRY_DSN is not set (safe for local development).
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  SentrySDK.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'development',
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [SentrySDK.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    beforeSend(event) {
      // Redact PII from extra context
      if (event.extra) {
        event.extra = redactObject(event.extra as Record<string, unknown>)
      }

      if (event.user) {
        event.user = redactObject(event.user as Record<string, unknown>)
      }

      // Redact PII from breadcrumb data
      if (Array.isArray(event.breadcrumbs)) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => ({
          ...breadcrumb,
          data:
            breadcrumb.data && typeof breadcrumb.data === 'object'
              ? redactObject(breadcrumb.data as Record<string, unknown>)
              : breadcrumb.data,
        }))
      }

      return event
    },
  })
}

/**
 * Capture an exception with optional non-PII context.
 * No-ops gracefully when Sentry is not configured.
 */
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return
  SentrySDK.captureException(error, context ? { extra: redactObject(context) } : undefined)
}
